import {
  useIsFetching,
  useMutation,
  useQueries,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { getMetrics } from '@/features/metrics/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { CLONE_TABLE_ROW_HEIGHT } from '@/features/shared/components/cloneTable/cloneTableConstants';
import { cloneTableSx } from '@/features/shared/components/cloneTable/cloneTableSx';
import { DeleteSuiteDialog } from '@/features/suites/components/dialogs';
import { suitesGridConfig } from '@/features/suites/grid/gridConfig';
import { useDeleteSuite, useUpdateSuite } from '@/features/suites/hooks/suites';
import { useNotifications } from '@/providers/notificationHooks';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import { queryKeys, useTagOptions } from '@/shared/hooks';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type DataGridProps, type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildSuitesColumns } from './suitesColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  QueryResult,
  SuiteExecutionStatus,
  SuiteListItemDto,
} from '@flowqa/shared';

type SuitesTableCloneProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
  queryKey?: QueryKey;
  initialData?: QueryResult<SuiteListItemDto>;
  onRowsChange?: (rows: SuiteListItemDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  onStatusSelect?: (status: SuiteExecutionStatus | null | undefined) => void;
  showDeltas?: boolean;
  tokenOverride?: string | null;
  disableLinks?: boolean;
  search?: string;
  sparklineRangeDays?: 7 | 14 | 30;
  metricsStartDay?: string | null;
  metricsEndDay?: string | null;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
  metricSeriesBySuiteIdOverride?: Map<
    string,
    {
      dates: Date[];
      passRate: number[];
      frequencyPerDay: number[];
      averageDurationMs: number[];
    }
  >;
  tagColorByNameOverride?: Partial<Record<string, TagColor>>;
};

const SuitesTableCloneComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = false,
  disableColumnMenu = false,
  showToolbar = true,
  noRowsText = 'No suites found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  initialData,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  onStatusSelect,
  showDeltas = true,
  tokenOverride,
  disableLinks = false,
  search,
  sparklineRangeDays = 7,
  metricsStartDay,
  metricsEndDay,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  metricSeriesBySuiteIdOverride,
  tagColorByNameOverride,
}: SuitesTableCloneProps): ReactElement => {
  const { session } = useAuth();
  const resolvedToken = tokenOverride ?? session?.tokens.accessToken ?? null;
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const queryClient = useQueryClient();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const deleteSuiteMutation = useDeleteSuite();
  const updateSuiteMutation = useUpdateSuite();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);
  const [visibleRows, setVisibleRows] = useState<SuiteListItemDto[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
    projectId: string;
  } | null>(null);
  const tagColorByName = useMemo(
    () =>
      tagOptions.reduce<Record<string, TagColor>>((acc, option) => {
        acc[option.value] = option.color;
        acc[option.value.trim().toLowerCase()] = option.color;
        return acc;
      }, {}),
    [tagOptions],
  );
  const resolvedTagColorByName = useMemo(
    () =>
      Object.entries({
        ...tagColorByName,
        ...(tagColorByNameOverride ?? {}),
      }).reduce<Record<string, TagColor>>((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {}),
    [tagColorByName, tagColorByNameOverride],
  );
  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) return;
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(
            item,
            resolvedToken ?? undefined,
            selectedOrganisationId ?? undefined,
          );
        }),
      );
    },
  });
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.suites]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const metricsRange = sparklineRangeDays === 14 ? '14d' : sparklineRangeDays === 30 ? '30d' : '7d';
  const metricsRangeKey =
    metricsStartDay && metricsEndDay ? `${metricsStartDay}_${metricsEndDay}` : 'all';
  const shouldQueryPerRowMetrics =
    resolvedToken !== null && metricSeriesBySuiteIdOverride === undefined;
  const perRowMetricsQueries = useQueries({
    queries: visibleRows.map((row) => ({
      queryKey: queryKeys.metrics(
        selectedOrganisationId ?? null,
        row.projectId,
        'suite',
        metricsRange,
        'all',
        'all',
        row.id,
        'all',
        metricsRangeKey,
      ),
      enabled: shouldQueryPerRowMetrics,
      staleTime: 30_000,
      queryFn: async () =>
        getMetrics(
          {
            range: metricsRange,
            scope: 'suite',
            statuses: [],
            projectId: row.projectId,
            entityId: row.id,
            startDay: metricsStartDay ?? null,
            endDay: metricsEndDay ?? null,
          },
          selectedOrganisationId ?? undefined,
          resolvedToken,
        ),
    })),
  });
  const metricSeriesBySuiteId = useMemo(() => {
    const result = new Map<
      string,
      {
        dates: Date[];
        passRate: number[];
        frequencyPerDay: number[];
        averageDurationMs: number[];
      }
    >();
    visibleRows.forEach((row, index) => {
      const queryData = perRowMetricsQueries[index]?.data;
      if (!queryData) return;
      const dates = queryData.metricsRangeDates.map((day) => dayjs(day).toDate());
      const passRate = queryData.series.filteredPassRate.map((point) => point.value);
      const frequencyPerDay = queryData.series.executionTrendMetrics.map((point) => point.value);
      const averageDurationMs = queryData.series.executionDurationAverage.map(
        (point) => point.value * 1000,
      );
      if (
        dates.length === 0 ||
        passRate.length !== dates.length ||
        frequencyPerDay.length !== dates.length ||
        averageDurationMs.length !== dates.length
      ) {
        return;
      }
      result.set(row.id, { dates, passRate, frequencyPerDay, averageDurationMs });
    });
    return result;
  }, [perRowMetricsQueries, visibleRows]);
  const resolvedMetricSeriesBySuiteId = metricSeriesBySuiteIdOverride ?? metricSeriesBySuiteId;
  const metricsQueriesReady =
    !shouldQueryPerRowMetrics ||
    visibleRows.length === 0 ||
    perRowMetricsQueries.every(
      (query) => query.data !== undefined || (!query.isPending && !query.isFetching),
    );
  const gridConfig = suitesGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'suites-table-clone' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
  });

  const suitesQueryKey = queryKey ?? gridConfig.queryKey;
  const isTableFetching = useIsFetching({ queryKey: suitesQueryKey }) > 0;

  const defaultColumns = useMemo(
    () =>
      buildSuitesColumns({
        selectedProjectId,
        onProjectSelect: setSelectedProjectId,
        onStatusSelect,
        thresholds: thresholds.suites,
        resolveThresholds,
        showDeltas,
        disableLinks,
        latestAsMeta: true,
        hideLatestPies: isTableFetching,
        includeTagsInNameColumn: true,
        includeTagsColumn: false,
        includeActionsColumn: true,
        includeLatestTestsColumn: false,
        includeLastExecutedColumn: false,
        sparklineRangeDays,
        passRateAsSparklineCell: true,
        frequencyAsSparklineCell: true,
        avgDurationAsSparklineCell: true,
        metricSeriesBySuiteId: resolvedMetricSeriesBySuiteId,
        onDeleteRow: (row) => {
          setDeleteTarget({ id: row.id, name: row.name, projectId: row.projectId });
        },
        deleteRowPendingId: deleteSuiteMutation.isPending ? (deleteTarget?.id ?? null) : null,
        tagColorByName: resolvedTagColorByName,
        onUpdateRowTagsColor: ({ row, tags, colors }) => {
          const normalizedTags = Array.from(
            new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
          );
          const normalizedColors = colors.slice(0, normalizedTags.length);
          setUpdatingTagRowId(row.id);
          void (async () => {
            try {
              await updateSuiteMutation.mutateAsync({ id: row.id, tags: normalizedTags });
              if (normalizedTags.length > 0) {
                await updateTagColorMutation.mutateAsync({
                  tags: normalizedTags,
                  colors: normalizedColors,
                });
              }
              notify({ message: 'Tags updated.', tone: 'green' });
              await queryClient.invalidateQueries({
                queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
              });
            } catch (error) {
              notify({ message: toErrorMessage(error), tone: 'red' });
            } finally {
              setUpdatingTagRowId(null);
            }
          })();
        },
        updateRowTagsColorPendingId:
          updateTagColorMutation.isPending || updateSuiteMutation.isPending
            ? updatingTagRowId
            : null,
      }),
    [
      resolvedTagColorByName,
      updateTagColorMutation,
      queryClient,
      resolvedMetricSeriesBySuiteId,
      deleteSuiteMutation.isPending,
      deleteTarget?.id,
      disableLinks,
      onStatusSelect,
      resolveThresholds,
      sparklineRangeDays,
      suitesQueryKey,
      isTableFetching,
      selectedProjectId,
      selectedOrganisationId,
      setSelectedProjectId,
      showDeltas,
      thresholds.suites,
      updateSuiteMutation,
      updatingTagRowId,
      notify,
      toErrorMessage,
    ],
  );
  const togglableColumnFields = useMemo(() => {
    const helperOnlyFields = new Set(['latestExecutionTimestamp']);
    return defaultColumns
      .map((column) => column.field)
      .filter((field) => !helperOnlyFields.has(field));
  }, [defaultColumns]);

  return (
    <>
      <ServerDataTable<SuiteListItemDto>
        queryPath="suites/query"
        {...gridConfig}
        queryFn={queryFn ?? gridConfig.queryFn}
        queryKey={queryKey ?? gridConfig.queryKey}
        initialData={initialData}
        columns={defaultColumns}
        getRowId={(row) => row.id}
        token={resolvedToken}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disablePageSizeSelect={disablePageSizeSelect}
        hideFooter={hideFooter}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        showToolbar={showToolbar}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        noRowsText={noRowsText}
        disableCellFocus
        getRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        showSkeletonOnFetch
        showLastUpdated={showLastUpdated}
        autoHeight
        externalLoading={!metricsQueriesReady}
        onRowsChange={(rows) => {
          setVisibleRows(rows);
          onRowsChange?.(rows);
        }}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        gridProps={{
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={cloneTableSx}
      />
      <DeleteSuiteDialog
        open={deleteTarget !== null}
        loading={deleteSuiteMutation.isPending}
        onClose={() => {
          if (deleteSuiteMutation.isPending) return;
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) return;
          const deletedSuiteName = deleteTarget.name;
          deleteSuiteMutation.mutate(
            { id: deleteTarget.id, projectId: deleteTarget.projectId },
            {
              onSuccess: () => {
                notify({ message: `Suite '${deletedSuiteName}' deleted.`, tone: 'green' });
                setDeleteTarget(null);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};

export const SuitesTableClone = memo(SuitesTableCloneComponent);
